/**
 * RewardClaimCard — et belønningsbytte som det store kortet i Godkjenn-køen
 * (#155, redesign steg 5a). Flyttet ut av RewardApprovalsRoute (Plan 02-12
 * Task 2 / UI-SPEC §7), som var Belønninger-fanen i den gamle køen; logikken
 * er den samme.
 *
 *   - Kortet viser belønningen, «{{kid}} vil bytte {{cost}} p mot
 *     {{rewardTitle}}» og når det ble bedt om, med Avvis / Godkjenn.
 *     `kid` er fortsatt medlems-id-en: navn på byttekortene er ny oppførsel 9
 *     (HANDOFF.md) og venter på bekreftelse.
 *   - On Godkjenn 422 reward_allergy_blocked → inline error (D-11 Layer 3
 *     visible UX).
 *   - Avvis åpner årsakene inline (RejectReasonPanel) med de belønnings-
 *     spesifikke chipene ['Ikke i denne uka', 'Foreslå noe annet', 'Annet'].
 *   - En mislykket avvisning vises inne i panelet, som står åpent så den
 *     voksne kan prøve igjen (#75). En vellykket lukker panelet; køen
 *     oppdateres når ['reward-claims', 'pending'] hentes på nytt.
 *
 * Kortet eier mutasjonene selv: det finnes bare ett stort kort om gangen.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/primitives/Button';
import { Icon } from '../../components/primitives/Icon';
import { RelativeTime } from '../../components/primitives/RelativeTime';
import { RejectReasonPanel } from '../approvals/RejectReasonPanel';
import { FEATURED_CARD, RewardTile } from '../approvals/queueParts';
import {
  useApproveRewardClaim,
  useRejectRewardClaim,
  type RewardClaimRow,
} from './useRewardsQueries';

interface RewardClaimCardProps {
  claim: RewardClaimRow;
  /** Tittelen fra katalogen; '—' når den ikke er lastet. */
  rewardTitle: string;
}

export function RewardClaimCard({ claim, rewardTitle }: RewardClaimCardProps): ReactNode {
  const { t } = useTranslation();
  const approve = useApproveRewardClaim();
  const reject = useRejectRewardClaim();

  const [approveError, setApproveError] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const approving = approve.isPending;
  const rejecting = reject.isPending;

  const handleApprove = (): void => {
    setApproveError(null);
    approve.mutate(claim.id, {
      onError: (err) => {
        const body = err.body as { error?: string; matched_keyword?: string } | undefined;
        setApproveError(
          body?.error === 'reward_allergy_blocked'
            ? t('rewards.approvals.error.allergyRaceGeneric')
            : t('rewards.approvals.error.generic'),
        );
      },
    });
  };

  const handleRejectSubmit = (reason: string): void => {
    setRejectError(null);
    reject.mutate(
      { claimId: claim.id, reason },
      {
        onSuccess: () => setRejectOpen(false),
        onError: () => setRejectError(t('rewards.approvals.error.generic')),
      },
    );
  };

  const rejectChips = [
    t('rewards.reject.reasons.thisWeek'),
    t('rewards.reject.reasons.suggestOther'),
    t('rewards.reject.reasons.other'),
  ];

  return (
    <article
      data-testid="reward-approval-card"
      data-claim-id={claim.id}
      aria-label={t('approvals.row.reward', { title: rewardTitle })}
      className={FEATURED_CARD}
    >
      <header className="flex items-center gap-2.5">
        <RewardTile />
        <p className="m-0 min-w-0 flex-1 text-caption text-ink-2">
          <RelativeTime instant={claim.claimed_at} />
        </p>
      </header>

      <div className="flex items-baseline justify-between gap-3">
        <h2 className="m-0 text-xl font-extrabold leading-tight text-ink">
          {t('approvals.row.reward', { title: rewardTitle })}
        </h2>
        <span className="shrink-0 text-base font-extrabold text-ink tnum">
          {t('approvals.row.rewardCost', { cost: claim.point_cost_at_claim })}
        </span>
      </div>

      <p className="m-0 text-body-s text-ink-2">
        {t('rewards.approvals.body', {
          kid: claim.member_id,
          cost: claim.point_cost_at_claim,
          rewardTitle,
        })}
      </p>

      {/* Ingen role: samme valg som RewardClaimSheet og RewardTemplateForm —
          feilen står ved knappen som ble trykket (T-04-toastTone). */}
      {approveError ? (
        <p
          data-testid="reward-approval-error"
          className="m-0 rounded-md border border-warn-line bg-warn-bg px-3 py-2 text-sm text-ink"
        >
          {approveError}
        </p>
      ) : null}

      {rejectOpen ? (
        <RejectReasonPanel
          testIdPrefix="reward-reject"
          prompt={t('rewards.reject.heading')}
          chips={rejectChips}
          submitLabel={t('rewards.reject.send')}
          submitting={rejecting}
          error={rejectError}
          onCancel={() => {
            setRejectOpen(false);
            setRejectError(null);
          }}
          onSubmit={handleRejectSubmit}
        />
      ) : (
        <footer className="flex gap-2.5">
          <Button
            variant="secondary"
            size={52}
            className="flex-1"
            data-testid="reward-approval-reject"
            disabled={approving || rejecting}
            onClick={() => {
              setRejectError(null);
              setRejectOpen(true);
            }}
          >
            {t('approvals.action.reject')}
          </Button>
          <Button
            variant="primary"
            size={52}
            className="flex-2"
            data-testid="reward-approval-approve"
            disabled={approving || rejecting}
            onClick={handleApprove}
          >
            {approving ? (
              <span
                data-testid="reward-approval-approve-spinner"
                aria-hidden
                className="inline-block h-3 w-3 animate-pulse rounded-full bg-on-primary"
              />
            ) : (
              <>
                <Icon.Check size={20} />
                {t('approvals.action.approve')}
              </>
            )}
          </Button>
        </footer>
      )}
    </article>
  );
}
